import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkle, ArrowsClockwise, Clock, LightningSlash } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { listCarousels } from "../lib/api";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listCarousels().then((d) => { setHistory(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const recent = history.slice(0, 6);

  return (
    <div className="px-6 md:px-10 pb-24" data-testid="dashboard">
      {/* Hero */}
      <section className="pt-16 md:pt-24 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
        <div className="md:col-span-7">
          <div className="kicker-label mb-6" data-testid="hero-kicker">
            <span className="inline-block w-6 h-px bg-forest align-middle mr-3" />
            {t("hero_kicker")}
          </div>
          <h1 className="font-heading font-semibold text-[48px] sm:text-[64px] lg:text-[84px] leading-[0.95] tracking-[-0.02em] text-ink">
            {t("hero_title_1")}
            <br />
            <span className="italic font-medium text-forest">{t("hero_title_2")}</span>
          </h1>
          <p className="mt-8 font-body text-ink-soft text-lg max-w-xl leading-relaxed">
            {t("hero_sub")}
          </p>

          <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-ctas">
            <Link
              to="/create"
              data-testid="cta-create"
              className="group inline-flex items-center gap-3 bg-forest text-cream rounded-full px-6 py-3.5 text-[15px] font-medium hover:bg-forest-dark transition-all hover:-translate-y-0.5"
            >
              <Sparkle weight="duotone" size={18} />
              {t("cta_create")}
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/recreate"
              data-testid="cta-recreate"
              className="inline-flex items-center gap-3 bg-cream-warm text-forest rounded-full px-6 py-3.5 text-[15px] font-medium hover:bg-[#E1DED5] transition"
            >
              <ArrowsClockwise weight="duotone" size={18} />
              {t("cta_recreate")}
            </Link>
          </div>

          <div className="mt-12 flex items-center gap-6 text-xs text-ink-soft">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-forest animate-pulse" />
              Claude Sonnet 4.5
            </div>
            <span>·</span>
            <div>{lang === "el" ? "Ελληνικά & Αγγλικά" : "Greek & English"}</div>
            <span>·</span>
            <div>1080 × 1080</div>
          </div>
        </div>

        {/* Right editorial art */}
        <div className="md:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative aspect-square max-w-[420px] ml-auto"
          >
            {/* Layered slide previews */}
            <div className="absolute inset-6 bg-cream-warm canvas-shadow" style={{ transform: "rotate(-3deg)" }} />
            <div className="absolute inset-3 bg-ink canvas-shadow flex items-center justify-center" style={{ transform: "rotate(1.5deg)" }}>
              <div className="text-cream text-center px-10">
                <div className="kicker-label text-cream/60 mb-4">01 / 10</div>
                <div className="font-heading font-semibold text-[28px] leading-[1.05] tracking-tight">
                  {lang === "el" ? "Γιατί οι δίαιτες σου αποτυγχάνουν κάθε φορά." : "Why your diets fail every single time."}
                </div>
              </div>
            </div>
            <div className="absolute inset-0 bg-cream canvas-shadow flex items-center justify-center" style={{ transform: "rotate(-1deg)" }}>
              <div className="text-ink text-center px-10">
                <div className="kicker-label text-forest mb-4">HOOK</div>
                <div className="font-heading font-semibold text-[30px] leading-[1.05] tracking-tight">
                  {lang === "el" ? "Σταμάτα να στερείσαι. Άρχισε να κερδίζεις." : "Stop starving. Start winning."}
                </div>
                <div className="mt-6 inline-block text-[11px] font-semibold tracking-[0.18em] uppercase text-forest border border-forest/40 rounded-full px-3 py-1">
                  {lang === "el" ? "Σωμα · Νους · Ζωη" : "Body · Mind · Life"}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee ticker */}
      <section className="mt-24 border-y border-line py-5 overflow-hidden" data-testid="marquee">
        <div className="flex gap-10 whitespace-nowrap marquee-track font-heading font-medium text-[20px] text-ink-soft">
          {Array.from({ length: 2 }).map((_, j) => (
            <React.Fragment key={j}>
              <span>Hooks that stop the scroll</span>
              <span className="text-forest">·</span>
              <span>Frameworks που μετατρέπουν</span>
              <span className="text-forest">·</span>
              <span>SEO captions</span>
              <span className="text-forest">·</span>
              <span>Ελληνικά · English</span>
              <span className="text-forest">·</span>
              <span>PNG · ZIP · PDF</span>
              <span className="text-forest">·</span>
              <span>Editorial design system</span>
              <span className="text-forest">·</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* History */}
      <section className="mt-24" data-testid="history-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="kicker-label mb-3">— {t("nav_history")}</div>
            <h2 className="font-heading text-[28px] md:text-[36px] font-medium text-ink tracking-tight">
              {t("section_latest")}
            </h2>
          </div>
          <Link
            to="/history"
            className="text-sm text-forest hover:text-forest-dark flex items-center gap-1.5"
            data-testid="history-view-all"
          >
            <Clock size={14} weight="duotone" />
            {t("nav_history")}
            <ArrowRight size={12} weight="bold" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="history-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-xl bg-cream-warm animate-pulse" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line bg-white/50 p-10 text-center" data-testid="history-empty">
            <LightningSlash size={28} className="mx-auto mb-3 text-ink-soft" weight="duotone" />
            <div className="font-heading text-lg text-ink mb-1">{t("empty_history_title")}</div>
            <div className="text-ink-soft text-sm">{t("empty_history_sub")}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="history-grid">
            {recent.map((c) => (
              <Link
                key={c.id}
                to={`/editor/${c.id}`}
                data-testid={`history-card-${c.id}`}
                className="group relative aspect-square overflow-hidden rounded-xl border border-line bg-white canvas-shadow hover:-translate-y-1 transition"
              >
                <MiniSlidePreview slide={c.slides?.[0]} />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white">
                  <div className="text-[10px] tracking-[0.12em] uppercase opacity-80">{c.language === "el" ? "EL" : "EN"} · {c.slides?.length || 0} slides</div>
                  <div className="text-sm font-medium truncate">{c.title}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MiniSlidePreview({ slide }) {
  if (!slide) return <div className="w-full h-full bg-cream-warm" />;
  return (
    <div
      className="w-full h-full flex items-center justify-center p-3 text-center"
      style={{ background: slide.bg_color || "#F9F8F6", color: slide.text_color || "#15251E" }}
    >
      <div className="font-heading font-semibold text-[13px] leading-[1.15] line-clamp-4">
        {slide.title || slide.kicker || "—"}
      </div>
    </div>
  );
}
